import React, { useState, useEffect } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { C, F, TTX } from './theme'
import { Card, SectionHead, Pill, Btn, Head, Icon, gbp } from './prims'
import { deriveModel, hasProposedSlot } from './derive'
import { CalendarContext } from './CalendarContext'
import { BusinessPlanCard } from './BusinessPlanCard'

// Inclusive ISO date range start..end (capped), for the proposed-slot picker.
function rangeDates(startIso: string, endIso: string): string[] {
  const s = new Date(`${startIso}T00:00:00Z`)
  const e = new Date(`${(endIso || startIso)}T00:00:00Z`)
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return []
  const out: string[] = []
  for (let d = new Date(s), i = 0; d <= e && i < 92; d.setUTCDate(d.getUTCDate() + 1), i++) out.push(d.toISOString().slice(0, 10))
  return out
}

const dateInput: React.CSSProperties = { padding: '7px 9px', border: `1px solid ${C.line}`, borderRadius: 8, fontFamily: F.body, fontSize: 13, color: C.ink, background: C.card }

// The proposed-slot picker — choose the days this idea would run. Saving resolves
// the running-timetable context + normal-day £ server-side (forecastContext).
function SlotPicker({ eventId, start, end }: { eventId: number; start: string | null; end: string | null }) {
  const { save } = useForecast(eventId)
  const [s, setS] = useState(start ?? '')
  const [e, setE] = useState(end ?? '')
  const [cal, setCal] = useState('') // which BP timetable calendar to model against ('' = current planned season)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  useEffect(() => { setS(start ?? ''); setE(end ?? '') }, [start, end])

  const dates = s ? rangeDates(s, e || s) : []
  const apply = async () => {
    setMsg(null)
    if (!s) { setMsg('Pick at least a start date.'); return }
    if (dates.length === 0) { setMsg('The end date must be on or after the start date.'); return }
    setSaving(true)
    try { await save({ proposed: { dates, ...(cal ? { calendar: cal } : {}) } }) } catch { setMsg('Could not save the proposed slot.') } finally { setSaving(false) }
  }

  // Calendar click → 2-click range: first click sets the start, a later day sets
  // the end; clicking again (or an earlier day) starts a fresh selection.
  const pick = (date: string) => {
    if (!s || (s && e)) { setS(date); setE(''); setMsg(null) }
    else if (date < s) { setS(date); setE('') }
    else { setE(date) }
  }

  return (
    <div style={{ marginBottom: 14 }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, padding: 12, borderRadius: 10, background: C.paper, border: `1px solid ${C.lineSoft}`, marginBottom: 12 }}>
      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontFamily: F.body, fontSize: 10.5, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>Start date</span>
        <input type="date" value={s} onChange={(ev) => setS(ev.target.value)} style={dateInput} />
      </label>
      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontFamily: F.body, fontSize: 10.5, fontWeight: 700, color: C.inkSoft, marginBottom: 5 }}>End date <span style={{ color: C.inkFaint, fontWeight: 400 }}>(optional)</span></span>
        <input type="date" value={e} min={s || undefined} onChange={(ev) => setE(ev.target.value)} style={dateInput} />
      </label>
      <Btn variant="primary" small icon="calendar" disabled={saving || !s} onClick={apply}>{saving ? 'Saving…' : (start ? 'Update slot' : 'Set proposed slot')}</Btn>
      {s && dates.length > 0 && <span style={{ fontFamily: F.body, fontSize: 12, color: C.inkFaint }}>{dates.length} day{dates.length === 1 ? '' : 's'}</span>}
      {msg && <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.oxblood }}>{msg}</span>}
    </div>
    <CalendarContext eventId={eventId} proposedDates={dates} onPickDay={pick} calendar={cal || undefined} onCalendarChange={setCal} />
    </div>
  )
}

// SCREEN EM-3 — Overview (Idea · viability). The at-a-glance landing tab for a
// Forecast Studio workspace: the concept brief, the proposed running slot and
// its timetable context, and four "lens" cards that jump to the deeper tabs.
// The net-incremental headline reads from deriveModel() so it agrees with the
// Model tab to the penny. All money is integer PENCE, formatted at the edge
// with gbp(); it is NOT financial-gated here (the number is the idea's whole
// point) — the deep money panels behind the lenses do their own gating.

type TabKey = 'overview' | 'baseline' | 'research' | 'model' | 'team'

// "Sat, 12 Jul" style label for a single proposed day chip.
function dayLabel(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

// "12 Jul – 14 Jul 2026" style window label from the first/last proposed date.
function windowLabel(startIso: string | null, endIso: string | null): string {
  const fmt = (iso: string) => {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }
  if (!startIso) return ''
  if (!endIso || endIso === startIso) return fmt(startIso)
  return `${fmt(startIso)} – ${fmt(endIso)}`
}

// One at-a-glance figure in the concept-brief right column.
function MiniStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ padding: '11px 13px', borderRadius: 10, background: C.paper, border: `1px solid ${C.lineSoft}` }}>
      <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: C.inkFaint }}>{label}</div>
      <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 800, color: C.ink, marginTop: 4, lineHeight: 1.15 }}>{value}</div>
    </div>
  )
}

// One clickable lens card — jumps to a deeper tab via onGoto. Uses a native
// button styled to match the Card prim (Card doesn't forward onClick), so the
// whole tile is a proper, keyboard-focusable click target.
function Lens({ icon, title, lines, onClick }: { icon: string; title: string; lines: [string, string]; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer',
        background: C.card, borderRadius: 12, border: `1px solid ${C.lineSoft}`,
        boxShadow: '0 1px 2px rgba(20,50,42,0.04)', padding: 18,
        display: 'flex', flexDirection: 'column', font: 'inherit'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={icon} size={17} color={C.green} />
        </span>
        <span style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 800, color: C.ink, letterSpacing: -0.2 }}>{title}</span>
        <span style={{ marginLeft: 'auto', display: 'flex' }}>
          <Icon name="chevR" size={18} color={C.inkFaint} />
        </span>
      </div>
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.inkSoft }}>{lines[0]}</div>
        <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkFaint }}>{lines[1]}</div>
      </div>
    </button>
  )
}

// Inline-editable concept brief. Persists to fc.brief.concept via PUT /forecast
// (partial merge). Seeds from the idea's description when concept is still empty.
function ConceptBriefEditor({ eventId, brief, fallback }: { eventId: number; brief: { sector: string; comps: string; questions: string; concept: string }; fallback: string }) {
  const { save } = useForecast(eventId)
  const [draft, setDraft] = useState(brief.concept || fallback)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { setDraft(brief.concept || fallback) }, [brief.concept, fallback])
  const dirty = draft.trim() !== (brief.concept || fallback).trim()
  const persist = async () => {
    if (!dirty) return
    setErr(null); setSaving(true)
    try { await save({ brief: { concept: draft.trim() } }) } catch { setErr('Could not save the concept brief.') } finally { setSaving(false) }
  }
  return (
    <div>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={persist}
        placeholder="Describe the idea — what is it, who is it for, why now?" rows={6}
        style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', padding: '11px 13px', border: `1px solid ${C.line}`, borderRadius: 10, fontFamily: F.body, fontSize: 14, lineHeight: 1.6, color: C.ink, background: C.card }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, minHeight: 16 }}>
        {saving && <span style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkFaint }}>Saving…</span>}
        {!saving && dirty && <span style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkFaint }}>Unsaved — click away to save</span>}
        {err && <span style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.oxblood }}>{err}</span>}
      </div>
    </div>
  )
}

export function OverviewTab({ eventId, onGoto }: { eventId: number; onGoto: (t: TabKey) => void }) {
  const { ev, fc } = useForecast(eventId)

  if (!fc) return null

  const slotted = hasProposedSlot(fc)
  const model = deriveModel(fc)
  const posInc = model.incrementalPence >= 0

  // --- Head sub-line: window dates · replaces N {ttLabel} days ------------------
  const ttLabel = fc.proposed.displacedTt ? (TTX[fc.proposed.displacedTt]?.label ?? '') : ''
  const win = windowLabel(fc.proposed.start, fc.proposed.end)
  const replaces = `replaces ${model.displacedCount} ${ttLabel || 'running'} day${model.displacedCount === 1 ? '' : 's'}`
  const sub = slotted ? [win, replaces].filter(Boolean).join(' · ') : 'No proposed slot yet'

  // --- Head right: NET INCREMENTAL stat ----------------------------------------
  const signed = (model.incrementalPence >= 0 ? '+ ' : '− ') + gbp(Math.abs(model.incrementalPence))
  const headStat = (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: C.inkFaint }}>Net incremental</div>
      <div style={{ fontFamily: F.serif, fontSize: 26, fontWeight: 800, lineHeight: 1.05, marginTop: 2, color: slotted ? (posInc ? C.green : C.oxblood) : C.inkFaint }}>
        {slotted ? signed : '—'}
      </div>
      {!slotted && <div style={{ fontFamily: F.body, fontSize: 11, color: C.inkFaint, marginTop: 3 }}>Set a proposed slot to model</div>}
    </div>
  )

  // --- Lens summary lines ------------------------------------------------------
  const linkedCount = fc.baseline.filter((b) => b.selected).length
  const insightCount = fc.insights.length
  const approvedCount = fc.team.filter((t) => t.approvalStatus === 'approved').length

  return (
    <div>
      <Head
        eyebrow="Idea · viability"
        title={ev?.name || 'Idea'}
        sub={sub}
        right={headStat}
      />

      {/* 1 — Concept brief */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHead icon="sparkle" label="Concept brief" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
          <ConceptBriefEditor eventId={eventId} brief={fc.brief} fallback={(ev?.description ?? '').trim()} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, alignContent: 'start' }}>
            <MiniStat label="Owner" value={ev?.manager?.name || '—'} />
            <MiniStat label="Forecast attendance" value={fc.attendanceFc != null ? fc.attendanceFc.toLocaleString('en-GB') : '—'} />
            <MiniStat label="Data source" value="Comparable events + research" />
          </div>
        </div>
      </Card>

      {/* 2 — Proposed slot & running timetable */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHead
          icon="calendar"
          label="Proposed slot & running timetable"
          right={<Pill fg={C.slate} bg={C.slateSoft} icon="layers">The Big Plan · {fc.season || '—'} season</Pill>}
        />
        <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, marginBottom: 14 }}>
          The days this idea would run, shown against the season's normal running timetable. Green days are the event's own proposed dates.
        </div>

        <SlotPicker eventId={eventId} start={fc.proposed.start} end={fc.proposed.end} />

        {fc.proposed.days.length > 0 ? (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
              {fc.proposed.days.map((d) => {
                const tt = TTX[d.tt] || TTX.none!
                return (
                  <div
                    key={d.date}
                    style={{
                      flex: '1 1 108px', minWidth: 98, borderRadius: 10, padding: '10px 11px',
                      border: `1.5px solid ${d.proposed ? C.green : C.lineSoft}`,
                      background: d.proposed ? C.greenBg : C.card
                    }}
                  >
                    <div style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.ink }}>{dayLabel(d.date)}</div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, padding: '2px 8px', borderRadius: 999, background: tt.bg, color: tt.fg, fontFamily: F.body, fontSize: 10.5, fontWeight: 800 }}>
                      <span style={{ width: 6, height: 6, borderRadius: 3, background: tt.dot }} />{tt.label}
                    </span>
                    {d.proposed && (
                      <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, color: C.green, marginTop: 7 }}>▲ proposed</div>
                    )}
                  </div>
                )
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
              <span style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft }}>
                Proposed window: {win || '—'} · {model.displacedCount} {(fc.proposed.displacedTt ? (TTX[fc.proposed.displacedTt]?.label ?? '') : '')} day{model.displacedCount === 1 ? '' : 's'}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                <Btn variant="ghost" small icon="layers" onClick={() => { /* Sprint 3 deep-link */ }}>View in The Big Plan</Btn>
              </span>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, background: C.brassBg, border: `1px solid ${C.brass}22` }}>
            <Icon name="info" size={16} color={C.brass} />
            <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.brassDk }}>
              No proposed slot yet — pick a start (and optional end) date above, then Set proposed slot to model this idea.
            </span>
          </div>
        )}
      </Card>

      {/* Market research summary (the research document) */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHead icon="sparkle" label="Market research" right={<Btn variant="ghost" small icon="chevR" onClick={() => onGoto('research')}>Open research</Btn>} />
        {fc.researchSummary ? (
          <>
            <div style={{ fontFamily: F.body, fontSize: 13.5, color: C.inkSoft, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{fc.researchSummary}</div>
            <div style={{ marginTop: 10, fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkFaint }}>{fc.insights.length} insight{fc.insights.length === 1 ? '' : 's'} accepted · used in the business plan</div>
          </>
        ) : (
          <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkFaint }}>No research yet — run market research to generate a summary and insights the business plan will draw on.</div>
        )}
      </Card>

      {/* Business plan — generate / upload / versions */}
      <BusinessPlanCard eventId={eventId} fc={fc} />

      {/* 3 — Lenses */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 }}>
        <Lens
          icon="layers"
          title="Baseline & data"
          lines={[
            `${linkedCount} comparable event${linkedCount === 1 ? '' : 's'} linked`,
            linkedCount > 0 ? 'Benchmark built from real financials' : 'Link events to build a benchmark'
          ]}
          onClick={() => onGoto('baseline')}
        />
        <Lens
          icon="sparkle"
          title="Market research"
          lines={[
            `${insightCount} insight${insightCount === 1 ? '' : 's'} accepted`,
            insightCount > 0 ? 'Findings shaping the forecast' : 'Run research to gather insights'
          ]}
          onClick={() => onGoto('research')}
        />
        <Lens
          icon="bolt"
          title="Model & scenarios"
          lines={[
            `Net incremental ${slotted ? signed : '—'}`,
            slotted ? 'Live sandbox drives the P&L' : 'Set a proposed slot to model'
          ]}
          onClick={() => onGoto('model')}
        />
        <Lens
          icon="users"
          title="Team & approval"
          lines={[
            `${fc.team.length} member${fc.team.length === 1 ? '' : 's'} · ${approvedCount} approved`,
            fc.team.length > 0 ? 'Delivery functions and sign-off' : 'Build the delivery team'
          ]}
          onClick={() => onGoto('team')}
        />
      </div>
    </div>
  )
}
